import { Button } from '@/components/ui/button'
import { STROKE_COLOR_OPTIONS } from './constants'
import type { DocumentAnnotationBounds } from './annotationGeometry'
import type { KonvaAnnotationTemplate } from './annotationSchema'

type KonvaAnnotationColorToolbarProps = {
  annotation: KonvaAnnotationTemplate
  bounds: DocumentAnnotationBounds
  onSelectColor: (color: string) => void
  selectedColor: string
}

const TOOLBAR_VERTICAL_OFFSET = 12

const AnnotationColorPreview = ({
  annotation,
  color,
}: {
  annotation: KonvaAnnotationTemplate
  color: string
}) => {
  const commonProps = {
    fill: 'none',
    stroke: color,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    strokeWidth: 3.5,
  }

  return (
    <svg viewBox="0 0 32 32" className="h-8 w-8" aria-hidden="true">
      {(annotation.kind === 'rect' || annotation.kind === 'roundRect') && (
        <rect
          x="6"
          y="6"
          width="20"
          height="20"
          rx={annotation.kind === 'roundRect' ? 5 : 1}
          {...commonProps}
        />
      )}
      {annotation.kind === 'circle' && (
        <circle cx="16" cy="16" r="10" {...commonProps} />
      )}
      {annotation.kind === 'ellipse' && (
        <ellipse cx="16" cy="16" rx="11" ry="8.5" {...commonProps} />
      )}
      {annotation.kind === 'triangle' && (
        <path d="M16 6 L26 24 H6 Z" {...commonProps} />
      )}
      {annotation.kind === 'diamond' && (
        <path d="M16 5 L27 16 L16 27 L5 16 Z" {...commonProps} />
      )}
      {annotation.kind === 'line' && <path d="M6 24 L26 8" {...commonProps} />}
      {annotation.kind === 'freehand' && (
        <path d="M5 22 C9 9 14 25 18 12 S25 8 27 18" {...commonProps} />
      )}
      {annotation.kind === 'arrow' && (
        <>
          <path d="M6 24 L23 10" {...commonProps} />
          <path d="M18 10 H23 V15" {...commonProps} />
        </>
      )}
      {annotation.kind === 'bubble' && (
        <>
          <circle cx="10" cy="22" r="4" {...commonProps} />
          <path d="M13.5 19 L25 8" {...commonProps} />
        </>
      )}
    </svg>
  )
}

const KonvaAnnotationColorToolbar = ({
  annotation,
  bounds,
  onSelectColor,
  selectedColor,
}: KonvaAnnotationColorToolbarProps) => (
  <div
    className="pointer-events-auto absolute z-[2] w-max max-w-none"
    style={{
      left: bounds.left + bounds.width / 2,
      top: bounds.top + bounds.height + TOOLBAR_VERTICAL_OFFSET,
      transform: 'translateX(-50%)',
    }}
    onMouseDown={(event) => {
      event.stopPropagation()
    }}
    onTouchStart={(event) => {
      event.stopPropagation()
    }}
  >
    <div className="grid w-max grid-cols-4 gap-1 rounded-md bg-slate-100/90 p-1 shadow-md backdrop-blur-sm">
      {STROKE_COLOR_OPTIONS.filter((color) => color !== selectedColor)
        .slice(0, 8)
        .map((color) => (
          <Button
            key={`annotation-stroke-color--${color}`}
            variant="outline"
            className="mx-0.5 flex items-center justify-center w-8 h-8 p-0.5"
            onClick={() => {
              onSelectColor(color)
            }}
          >
            <AnnotationColorPreview annotation={annotation} color={color} />
          </Button>
        ))}
    </div>
  </div>
)

export default KonvaAnnotationColorToolbar
